import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../lib/trpc';

export interface TableColumnPrefs {
  /** User's saved column order (by DataTableColumn.key). Empty = defaults. */
  order: string[];
  /** Keys the user has hidden. */
  hidden: string[];
  /** Persist a new full column order (called by the header drag handler). */
  setOrder: (nextOrder: string[]) => void;
  /** Toggle a single column's visibility (called by the columns menu). */
  toggleHidden: (key: string) => void;
  /** Clear the saved override for this table (restore shipped defaults). */
  reset: () => void;
  /** True once the saved prefs have loaded (or the feature is disabled). */
  isLoaded: boolean;
}

/**
 * Per-user, per-table column order + visibility, persisted via `trpc.userPrefs.*`.
 *
 * Ported from Sonar (102) — the logic is verbatim; only the tRPC surface is this app's
 * (`useTRPC()` + queryOptions, not the classic hooks). Its contract, kept whole:
 * - `tableId === undefined` disables the feature: stable empty arrays, no-op
 *   persistence, no network call — so DataTable can call the hook unconditionally and
 *   every legacy call site (no `tableId`) stays 100% inert.
 * - Writes fire ONLY on an explicit user action (setOrder / toggleHidden / reset),
 *   never on passive reconciliation — reconciliation against the live column set
 *   happens at render in deriveOrderedColumns, not here. Saves are debounced (600ms)
 *   with a REAL flush on unmount AND on `tableId` change; the pending payload carries
 *   its own `tableId` so a flush that fires mid-transition writes under the right table.
 */
export function useTableColumnPrefs(
  tableId: string | undefined,
  // Keys that ship HIDDEN by default (in the picker, off until the user turns them on).
  // Seeded as the starting `hidden` set for a user who has never saved prefs for this
  // table; once they customize, their saved set wins verbatim.
  defaultHidden: string[] = [],
): TableColumnPrefs {
  const enabled = !!tableId;

  const trpc = useTRPC();
  const qc = useQueryClient();
  const query = useQuery(
    trpc.userPrefs.getTable.queryOptions(
      { tableId: tableId ?? '' },
      { enabled, staleTime: 5 * 60_000, refetchOnWindowFocus: false },
    ),
  );
  const setMutation = useMutation(trpc.userPrefs.setTable.mutationOptions());
  // EVERY in-flight save, so reset can SEQUENCE behind all of them — two unordered
  // mutations on the wire let a dispatched save land after the delete and resurrect
  // the row the user just reset, and with overlapping saves the OLDER one can outlive
  // the newer, so tracking only the newest is not enough (found by the 102 adversary,
  // in two passes).
  const savesInFlight = useRef<Set<Promise<unknown>>>(new Set());
  const resetMutation = useMutation(trpc.userPrefs.resetTable.mutationOptions());

  // Ref so the seeding effects/reset always read the latest defaultHidden without
  // re-subscribing every render (the array identity changes each parent render).
  const defaultHiddenRef = useRef(defaultHidden);
  defaultHiddenRef.current = defaultHidden;

  const [state, setState] = useState<{ order: string[]; hidden: string[] }>({
    order: [],
    hidden: defaultHidden,
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  // Which tableId the user has ACTED on this session. Sonar hydrates once per tableId
  // ("hydratedFor") — correct there, wrong here: this app PERSISTS the query cache to
  // IndexedDB, so after a reload the first `query.data` is yesterday's answer, the
  // hydrate-once guard marks the table done, and the live response that arrives a beat
  // later is discarded — saved prefs revert on every reload (measured: a saved order
  // survived the write and died at the next hydrate). So: hydrate on EVERY new server
  // answer, unless the user has acted this session — their in-session edits stay
  // authoritative, which is the only protection hydrate-once actually provided.
  // Servwell patch (102) #1 of 2 — hydrate-until-interaction replaces Sonar's
  // hydrate-once. See the divergence account above; guarded by table-prefs-hook.test.ts.
  const interactedOn = useRef<string | null>(null);

  // Drop stale state the moment tableId changes so a previous table's order can't
  // linger. Seed with the table's default-hidden set so the first paint hides them.
  useEffect(() => {
    interactedOn.current = null;
    setState({ order: [], hidden: defaultHiddenRef.current });
  }, [tableId]);

  // Hydrate from every server answer until the user acts. When the user has NEVER
  // saved prefs for this table (`customized === false`), seed `hidden` with the
  // table's default-hidden set instead of the server's empty array; once they've
  // saved, their set is authoritative (even if it's empty = "show everything").
  useEffect(() => {
    if (!enabled) return;
    if (interactedOn.current === tableId) return;
    if (query.data) {
      const customized = query.data.customized ?? false;
      setState({
        order: query.data.orderedKeys ?? [],
        hidden: customized ? (query.data.hiddenKeys ?? []) : defaultHiddenRef.current,
      });
    }
  }, [enabled, tableId, query.data]);

  // ── Debounced persistence with a real flush ─────────────────────────
  const pending = useRef<{ tableId: string; order: string[]; hidden: string[] } | null>(
    null,
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mutateRef = useRef(setMutation.mutateAsync);
  mutateRef.current = setMutation.mutateAsync;
  const qcRef = useRef(qc);
  qcRef.current = qc;
  const trpcRef = useRef(trpc);
  trpcRef.current = trpc;

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const p = pending.current;
    if (p) {
      pending.current = null;
      // Write the getTable cache FIRST, then save. This app persists the query cache to
      // IndexedDB and getTable carries a 5-minute staleTime, so a reload restores the
      // cached answer and refetches nothing — measured: a saved hide came back visible
      // because the only data the next pageview ever saw was the stale persisted entry.
      // The cache must therefore carry the latest saved state the moment it exists.
      // customized: true — this IS a customization; hydrate must not re-seed defaults.
      // Servwell patch (102) #2 of 2 — cache write-through, BEFORE the wire. The
      // persisted cache is what a reload restores; it must carry the latest saved state.
      qcRef.current.setQueryData(
        trpcRef.current.userPrefs.getTable.queryOptions({ tableId: p.tableId }).queryKey,
        { orderedKeys: p.order, hiddenKeys: p.hidden, customized: true },
      );
      const run: Promise<unknown> = mutateRef
        .current({ tableId: p.tableId, orderedKeys: p.order, hiddenKeys: p.hidden })
        .catch(() => {
          // A failed save must not keep claiming persistence: invalidate so the next
          // fetch snaps the layout back to the server's truth instead of lying until
          // the persisted entry happens to go stale.
          void qcRef.current.invalidateQueries({
            queryKey: trpcRef.current.userPrefs.getTable.queryOptions({
              tableId: p.tableId,
            }).queryKey,
          });
        })
        .finally(() => {
          savesInFlight.current.delete(run);
        });
      savesInFlight.current.add(run);
    }
  }, []);

  const schedule = useCallback(
    (order: string[], hidden: string[]) => {
      if (!tableId) return;
      pending.current = { tableId, order, hidden };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, 600);
    },
    [tableId, flush],
  );

  // Flush on unmount AND on tableId change (persist the prior table's pending edit).
  useEffect(() => () => flush(), [tableId, flush]);

  // Any explicit user action freezes hydration for this table: a still-in-flight (or
  // later-arriving) response must not overwrite what the user just did.
  const markInteracted = useCallback(() => {
    interactedOn.current = tableId ?? null;
  }, [tableId]);

  const setOrder = useCallback(
    (nextOrder: string[]) => {
      markInteracted();
      const hidden = stateRef.current.hidden;
      setState({ order: nextOrder, hidden });
      schedule(nextOrder, hidden);
    },
    [schedule, markInteracted],
  );

  const toggleHidden = useCallback(
    (key: string) => {
      markInteracted();
      const cur = stateRef.current;
      const hidden = cur.hidden.includes(key)
        ? cur.hidden.filter((k) => k !== key)
        : [...cur.hidden, key];
      setState({ order: cur.order, hidden });
      schedule(cur.order, hidden);
    },
    [schedule, markInteracted],
  );

  const reset = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    pending.current = null;
    // Reset is a user ACTION: freeze hydration (an in-flight getTable response from
    // before the reset must not re-apply the prefs the user just cleared) and cancel
    // that in-flight read so it cannot stomp the cache write below either.
    markInteracted();
    if (tableId) {
      void qc.cancelQueries({
        queryKey: trpc.userPrefs.getTable.queryOptions({ tableId }).queryKey,
      });
    }
    // "Restore defaults" = the SHIPPED defaults: default-hidden columns hidden again.
    setState({ order: [], hidden: defaultHiddenRef.current });
    if (tableId) {
      // Cache first, for the same reason flush() writes it: the persisted cache is what
      // a reload restores, and it must never resurrect prefs the user just reset.
      // customized:false so a fresh hydrate re-seeds defaultHidden.
      qc.setQueryData(trpc.userPrefs.getTable.queryOptions({ tableId }).queryKey, {
        orderedKeys: [],
        hiddenKeys: [],
        customized: false,
      });
      // SEQUENCE behind EVERY in-flight save — unordered, any one of them can land
      // after the delete and resurrect the row.
      const fire = () =>
        resetMutation.mutate(
          { tableId },
          {
            // Same honesty as flush(): a failed reset snaps back rather than lying.
            onError: () => {
              void qc.invalidateQueries({
                queryKey: trpc.userPrefs.getTable.queryOptions({ tableId }).queryKey,
              });
            },
          },
        );
      if (savesInFlight.current.size > 0) {
        void Promise.allSettled([...savesInFlight.current]).then(fire);
      } else {
        fire();
      }
    }
  }, [tableId, resetMutation, qc, trpc, markInteracted]);

  return {
    order: state.order,
    hidden: state.hidden,
    setOrder,
    toggleHidden,
    reset,
    isLoaded: !enabled || query.isSuccess,
  };
}
